/** Chart primitives for the workspace and macro mockups.
 *
 * Every chart sits inside FixedChart, which owns an explicit pixel height and
 * feeds an integer width to the Recharts root. No ResponsiveContainer, no
 * percentage-height parents, so there is no ResizeObserver feedback loop.
 * Derived series are memoised by the callers.
 */
import { useMemo } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ReferenceLine, Tooltip, XAxis, YAxis,
} from "recharts";
import { FixedChart } from "@/cockpit/FixedChart";
import { AXIS, GRID, fmtMd, tooltipBox } from "@/cockpit/formatCockpit";

/** Same palette the live workbench uses, so the mock reads as the same product. */
export const REGIME_COLORS = {
  "Quiet Range": "#3B82F6",
  "Bullish Trend": "#10B981",
  "Bearish Trend": "#EF4444",
  Volatile: "#F59E0B",
  transitioning: "#71717A",
};

export const REGIME_SEMANTICS = {
  "Quiet Range": "Low variance, mean-reverting. Low IV — short premium pays thin; long premium is cheap.",
  "Bullish Trend": "Sustained upward drift with positive momentum. Favours long-delta and put-side income.",
  "Bearish Trend": "Sustained downward drift. Favours call-side income and protective hedges.",
  Volatile: "High variance / elevated IV — direction-agnostic. Premium is rich, but expected moves are large.",
};

const TREND_COLORS = { bullish: "#10B981", sideways: "#71717A", bearish: "#EF4444" };

/** Regime ribbon. Colour carries identity, but every band is also labelled. */
export function RegimeRibbon({ ribbon, total, testid }) {
  const spans = useMemo(() => {
    const n = total || ribbon.reduce((a, b) => a + (b.bars || 1), 0);
    return ribbon.map((r) => ({ ...r, pct: ((r.bars || 1) / n) * 100 }));
  }, [ribbon, total]);
  return (
    <div data-testid={testid}>
      <div className="flex h-3 w-full overflow-hidden rounded-sm" style={{ background: "var(--hoip-hover)" }}>
        {spans.map((s, i) => (
          <div
            key={`${s.state}-${s.from}-${i}`}
            title={`${s.state} · ${s.from} → ${s.to}`}
            style={{
              width: `${s.pct}%`,
              background: REGIME_COLORS[s.state] || "#71717A",
              opacity: 0.55,
              borderRight: i < spans.length - 1 ? "1px solid var(--hoip-bg)" : "none",
            }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        {spans.map((s, i) => (
          <span key={`lg-${s.state}-${s.from}-${i}`} className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: REGIME_COLORS[s.state] }} />
            {s.state} · {s.from}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Trend tape from technicals, drawn as discrete blocks with a text legend. */
export function TrendRibbon({ series, testid }) {
  const blocks = useMemo(() => {
    const out = [];
    series.forEach((r) => {
      const t = r.close > (r.sma50 ?? r.close) ? (r.macdHistogram >= 0 ? "bullish" : "sideways") : (r.macdHistogram < 0 ? "bearish" : "sideways");
      const last = out[out.length - 1];
      if (last && last.trend === t) last.bars += 1;
      else out.push({ trend: t, bars: 1, from: r.date });
    });
    return out;
  }, [series]);
  const total = series.length || 1;
  return (
    <div data-testid={testid}>
      <div className="flex h-2 w-full overflow-hidden rounded-sm" style={{ background: "var(--hoip-hover)" }}>
        {blocks.map((b, i) => (
          <div
            key={`${b.trend}-${b.from}-${i}`}
            title={`${b.trend} · from ${b.from}`}
            style={{ width: `${(b.bars / total) * 100}%`, background: TREND_COLORS[b.trend], opacity: 0.5 }}
          />
        ))}
      </div>
      <div className="flex gap-3 mt-1 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        <span style={{ color: TREND_COLORS.bullish }}>▲ Bullish</span>
        <span>■ Sideways</span>
        <span style={{ color: TREND_COLORS.bearish }}>▼ Bearish</span>
      </div>
    </div>
  );
}

export function PriceLineChart({ series, height = 160, showSma = false, testid }) {
  const data = useMemo(
    () => series.map((r) => ({ date: r.date, close: r.close, sma50: r.sma50, sma200: r.sma200 })),
    [series],
  );
  return (
    <div data-testid={testid}>
      <FixedChart height={height}>
        {(width) => (
          <LineChart width={width} height={height} data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: AXIS, fontSize: 9 }} tickFormatter={fmtMd} minTickGap={34} />
            <YAxis tick={{ fill: AXIS, fontSize: 9 }} width={44} domain={["auto", "auto"]} />
            <Tooltip contentStyle={tooltipBox} />
            <Line dataKey="close" stroke="#E4E4E7" dot={false} strokeWidth={1.4} isAnimationActive={false} />
            {showSma ? (
              <Line dataKey="sma50" stroke="#93C5FD" dot={false} strokeWidth={1} strokeDasharray="4 3" isAnimationActive={false} />
            ) : null}
            {showSma ? (
              <Line dataKey="sma200" stroke="#71717A" dot={false} strokeWidth={1} strokeDasharray="2 3" isAnimationActive={false} />
            ) : null}
          </LineChart>
        )}
      </FixedChart>
    </div>
  );
}

/** Candles with SMA20/50/200 and Bollinger bands, drawn as plain SVG. */
export function CandleChart({ series, height = 280, levels, extraLines, smaLegend, testid }) {
  const smaKeys = smaLegend === undefined ? ["sma20", "sma50", "sma200"] : smaLegend;
  return (
    <div data-testid={testid}>
      <FixedChart height={height}>
        {(width) => {
          const padL = 44;
          const padR = 10;
          const padT = 8;
          const padB = 18;
          const w = Math.max(60, width - padL - padR);
          const h = height - padT - padB;
          const lows = series.map((r) => r.low).concat((levels?.support || []).map((s) => s.level));
          const highs = series.map((r) => r.high).concat((levels?.resistance || []).map((s) => s.level));
          const min = Math.min(...lows) * 0.995;
          const max = Math.max(...highs) * 1.005;
          const y = (v) => padT + h - ((v - min) / (max - min)) * h;
          const step = w / series.length;
          const bw = Math.max(1, Math.min(6, step * 0.6));
          const path = (key) => series
            .map((r, i) => (r[key] == null ? null : `${i === 0 || series[i - 1][key] == null ? "M" : "L"}${padL + i * step + step / 2},${y(r[key])}`))
            .filter(Boolean)
            .join(" ");
          const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => min + (max - min) * f);
          return (
            <svg width={width} height={height} role="img" aria-label="Price candles with moving averages">
              {ticks.map((t) => (
                <g key={t}>
                  <line x1={padL} x2={padL + w} y1={y(t)} y2={y(t)} stroke={GRID} strokeDasharray="3 3" />
                  <text x={padL - 5} y={y(t) + 3} textAnchor="end" fill={AXIS} fontSize="9">{t.toFixed(0)}</text>
                </g>
              ))}
              <path d={path("bbUpper")} fill="none" stroke="#3F3F46" strokeWidth="1" strokeDasharray="3 3" />
              <path d={path("bbLower")} fill="none" stroke="#3F3F46" strokeWidth="1" strokeDasharray="3 3" />
              {series.map((r, i) => {
                const x = padL + i * step + step / 2;
                const up = r.close >= r.open;
                const c = up ? "#10B981" : "#EF4444";
                const top = y(Math.max(r.open, r.close));
                const bot = y(Math.min(r.open, r.close));
                return (
                  <g key={r.date}>
                    <line x1={x} x2={x} y1={y(r.high)} y2={y(r.low)} stroke={c} strokeWidth="1" />
                    <rect
                      x={x - bw / 2}
                      y={top}
                      width={bw}
                      height={Math.max(1, bot - top)}
                      fill={up ? c : "none"}
                      stroke={c}
                      strokeWidth="1"
                    />
                  </g>
                );
              })}
              {smaKeys.includes("sma20") ? <path d={path("sma20")} fill="none" stroke="#FBBF24" strokeWidth="1.1" /> : null}
              {smaKeys.includes("sma50") ? <path d={path("sma50")} fill="none" stroke="#93C5FD" strokeWidth="1.1" /> : null}
              {smaKeys.includes("sma200") ? <path d={path("sma200")} fill="none" stroke="#A1A1AA" strokeWidth="1.1" strokeDasharray="4 3" /> : null}
              {(extraLines || []).map((ln) => (
                <path key={ln.key} d={path(ln.key)} fill="none" stroke={ln.color} strokeWidth="1.1" strokeDasharray={ln.dash || undefined} />
              ))}
              {(levels?.resistance || []).map((r) => (
                <g key={`r-${r.level}`}>
                  <line x1={padL} x2={padL + w} y1={y(r.level)} y2={y(r.level)} stroke="#EF4444" strokeWidth="1" strokeDasharray="5 4" opacity="0.7" />
                  <text x={padL + w} y={y(r.level) - 3} textAnchor="end" fill="#EF4444" fontSize="8">▼ {r.label}</text>
                </g>
              ))}
              {(levels?.support || []).map((s) => (
                <g key={`s-${s.level}`}>
                  <line x1={padL} x2={padL + w} y1={y(s.level)} y2={y(s.level)} stroke="#10B981" strokeWidth="1" strokeDasharray="5 4" opacity="0.7" />
                  <text x={padL + w} y={y(s.level) + 9} textAnchor="end" fill="#10B981" fontSize="8">▲ {s.label}</text>
                </g>
              ))}
            </svg>
          );
        }}
      </FixedChart>
      <div className="flex flex-wrap gap-3 mt-1 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        {smaKeys.includes("sma20") ? <span style={{ color: "#FBBF24" }}>— SMA20</span> : null}
        {smaKeys.includes("sma50") ? <span style={{ color: "#93C5FD" }}>— SMA50</span> : null}
        {smaKeys.includes("sma200") ? <span style={{ color: "#A1A1AA" }}>-- SMA200</span> : null}
        {(extraLines || []).map((ln) => (
          <span key={`lg-${ln.key}`} style={{ color: ln.color }}>{ln.dash ? "--" : "—"} {ln.label}</span>
        ))}
        <span>·· Bollinger 20/2</span>
        <span style={{ color: "#10B981" }}>▲ Support</span>
        <span style={{ color: "#EF4444" }}>▼ Resistance</span>
      </div>
    </div>
  );
}

export function VolumeChart({ series, height = 70, testid }) {
  const data = useMemo(() => series.map((r) => ({ date: r.date, volume: r.volume, up: r.close >= r.open })), [series]);
  return (
    <div data-testid={testid}>
      <FixedChart height={height}>
        {(width) => (
          <BarChart width={width} height={height} data={data} margin={{ top: 4, right: 10, left: 0, bottom: 0 }}>
            <XAxis dataKey="date" hide />
            <YAxis tick={{ fill: AXIS, fontSize: 8 }} width={44} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} />
            <Tooltip contentStyle={tooltipBox} labelFormatter={fmtMd} />
            <Bar dataKey="volume" isAnimationActive={false}>
              {data.map((d) => <Cell key={d.date} fill={d.up ? "#10B98155" : "#EF444455"} />)}
            </Bar>
          </BarChart>
        )}
      </FixedChart>
    </div>
  );
}

export function OscillatorChart({ series, dataKey, height = 110, bands = [], domain = ["auto", "auto"], testid }) {
  const data = useMemo(() => series.map((r) => ({ date: r.date, [dataKey]: r[dataKey] })), [series, dataKey]);
  return (
    <div data-testid={testid}>
      <FixedChart height={height}>
        {(width) => (
          <LineChart width={width} height={height} data={data} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: AXIS, fontSize: 8 }} tickFormatter={fmtMd} minTickGap={40} />
            <YAxis tick={{ fill: AXIS, fontSize: 8 }} width={44} domain={domain} />
            <Tooltip contentStyle={tooltipBox} />
            {bands.map((b) => (
              <ReferenceLine key={b} y={b} stroke="#3F3F46" strokeDasharray="3 3" />
            ))}
            <Line dataKey={dataKey} stroke="#93C5FD" dot={false} strokeWidth={1.2} isAnimationActive={false} />
          </LineChart>
        )}
      </FixedChart>
    </div>
  );
}

export function HistogramChart({ series, dataKey, height = 100, testid }) {
  const data = useMemo(() => series.map((r) => ({ date: r.date, [dataKey]: r[dataKey] })), [series, dataKey]);
  return (
    <div data-testid={testid}>
      <FixedChart height={height}>
        {(width) => (
          <BarChart width={width} height={height} data={data} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: AXIS, fontSize: 8 }} tickFormatter={fmtMd} minTickGap={40} />
            <YAxis tick={{ fill: AXIS, fontSize: 8 }} width={44} />
            <Tooltip contentStyle={tooltipBox} />
            <ReferenceLine y={0} stroke="#3F3F46" />
            <Bar dataKey={dataKey} isAnimationActive={false}>
              {data.map((d) => <Cell key={d.date} fill={d[dataKey] >= 0 ? "#10B981" : "#EF4444"} />)}
            </Bar>
          </BarChart>
        )}
      </FixedChart>
    </div>
  );
}

export function MultiLineChart({ data, lines, height = 180, xKey = "date", yFormatter, testid }) {
  return (
    <div data-testid={testid}>
      <FixedChart height={height}>
        {(width) => (
          <LineChart width={width} height={height} data={data} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey={xKey} tick={{ fill: AXIS, fontSize: 9 }} minTickGap={30} />
            <YAxis tick={{ fill: AXIS, fontSize: 9 }} width={46} tickFormatter={yFormatter} domain={["auto", "auto"]} />
            <Tooltip contentStyle={tooltipBox} />
            {lines.map((l) => (
              <Line
                key={l.key}
                dataKey={l.key}
                name={l.label}
                stroke={l.color}
                strokeDasharray={l.dash}
                dot={false}
                strokeWidth={1.3}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        )}
      </FixedChart>
      <div className="flex flex-wrap gap-3 mt-1 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        {lines.map((l) => (
          <span key={`lg-${l.key}`} style={{ color: l.color }}>{l.dash ? "--" : "—"} {l.label}</span>
        ))}
      </div>
    </div>
  );
}

export function SimpleBarChart({ data, dataKey, xKey = "period", height = 150, formatter, testid }) {
  return (
    <div data-testid={testid}>
      <FixedChart height={height}>
        {(width) => (
          <BarChart width={width} height={height} data={data} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey={xKey} tick={{ fill: AXIS, fontSize: 9 }} />
            <YAxis tick={{ fill: AXIS, fontSize: 9 }} width={46} tickFormatter={formatter} />
            <Tooltip contentStyle={tooltipBox} />
            <Bar dataKey={dataKey} fill="#52525B" isAnimationActive={false} />
          </BarChart>
        )}
      </FixedChart>
    </div>
  );
}
